import Papa from 'papaparse';

export const normalizeHeader = (header) => String(header ?? '').trim().toLowerCase();

export const buildTimestamp = () => new Date().toISOString().replace(/[:.]/g, '-');

export const downloadTextFile = ({ content, filename, mimeType }) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const unparseCsvWithBom = ({ rows, columns }) => {
  const csvBody = Papa.unparse(rows, {
    columns,
    newline: '\r\n',
  });
  // Excel on Windows often needs a UTF-8 BOM to display Hebrew correctly.
  return `\uFEFF${csvBody}`;
};

export const containsReplacementChar = (value) => String(value ?? '').includes('\uFFFD');

export function parseCsvFileWithEncodingFallback({
  file,
  requiredHeaders,
  parseRow,
  encodings = ['utf-8', 'windows-1255'],
  shouldRetryWithNextEncoding,
}) {
  const parseWithEncoding = (encoding) =>
    new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: normalizeHeader,
        encoding,
        complete: (results) => {
          try {
            const fields = (results?.meta?.fields || [])
              .map((f) => String(f ?? '').trim().toLowerCase())
              .filter(Boolean);

            const missingRequired = (requiredHeaders || []).filter((h) => !fields.includes(h));
            if (missingRequired.length) {
              reject(new Error(`CSV is missing required header: ${missingRequired.join(', ')}`));
              return;
            }

            const papaErrors = (results.errors || []).map((e) => {
              const row = typeof e.row === 'number' ? `row ${e.row + 1}` : 'unknown row';
              return `Parse error (${row}): ${e.message}`;
            });

            const rowErrors = [];
            const imported = [];

            (results.data || []).forEach((row, idx) => {
              const rowNumber = idx + 2;
              const parsed = parseRow(row, rowNumber);
              if (parsed?.error) rowErrors.push(parsed.error);
              if (parsed?.value) imported.push(parsed.value);
            });

            const allErrors = [...papaErrors, ...rowErrors];
            if (allErrors.length) {
              reject(new Error(allErrors.join('\n')));
              return;
            }

            resolve(imported);
          } catch (err) {
            reject(err);
          }
        },
        error: reject,
      });
    });

  const retryFn =
    typeof shouldRetryWithNextEncoding === 'function'
      ? shouldRetryWithNextEncoding
      : () => false;

  return (async () => {
    let lastResult = [];
    for (let i = 0; i < encodings.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const imported = await parseWithEncoding(encodings[i]);
      lastResult = imported;
      const shouldRetry = retryFn(imported);
      if (!shouldRetry || i === encodings.length - 1) {
        return imported;
      }
    }
    return lastResult;
  })();
}
